<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useBreakpoint } from "@/design/composables/use-breakpoint";
import Icon from "@/design/primitives/Icon.vue";
import Tooltip from "@/design/primitives/Tooltip.vue";
import type { OrbState } from "@/shared/agent-display/BenOrb.vue";
import { type SessionSubject, useSessions } from "../api/use-agents";
import { useBen } from "../api/use-ben";
import { useBenThread } from "../composables/use-ben-thread";
import type { NavigateTarget, PreparedDraft } from "../composables/use-turn-stream";
import { useAgentsI18n } from "../i18n/use-agents-i18n";
import DockHeader from "./DockHeader.vue";
import SessionList from "./SessionList.vue";
import StreamingChat from "./StreamingChat.vue";

/** Shared Ben dock. The server determines whether this is panel or conversation context. */
const props = defineProps<{
	/**
	 * O assunto desta doca: a conversa aberta, ou nada — que é o painel.
	 *
	 * Quem o monta é a PÁGINA, porque `features/inbox` não lê `features/agents`.
	 */
	subject?: SessionSubject | null;
	/** Identidade explícita para abrir conversas históricas. */
	preferred?: string | null;
	/** A janela ocupando a tela — a gaveta oferece, a coluna não. */
	wide?: boolean;
	widenable?: boolean;
}>();

const emit = defineEmits<{
	/** O que o Ben está a fazer, para o orbe continuar a respirar com a doca fechada. */
	state: [OrbState];
	/** O texto de uma bolha, pedido para o rascunho da conversa (um CLIQUE). */
	useAsDraft: [text: string];
	/** O rascunho que o Ben preparou sozinho: palavras e, se houver, o arquivo. */
	preparedDraft: [draft: PreparedDraft];
	navigate: [target: NavigateTarget];
	close: [];
	toggleWide: [];
}>();

const { t } = useAgentsI18n();

/**
 * Nomes DIFERENTES dos das propriedades de propósito: no template do Vue as duas
 * famílias partilham um espaço de nomes, e `subject` a apontar para si próprio é
 * a espécie de sombra que não dá erro nenhum — só uma tela errada.
 */
const about = computed(() => props.subject ?? null);
const chosen = computed(() => props.preferred ?? null);

const { data, isPending } = useBen({ subject: about, preferred: chosen });

const ben = computed(() => data.value?.ben ?? null);
/** Antes da primeira resposta a porta é a do painel: é a que não promete nada. */
const door = computed(() => data.value?.door ?? "panel");
/** O `subject` só desce depois de o SERVIDOR ter dito que esta é a porta da conversa. */
const anchor = computed(() => (door.value === "conversation" ? about.value : null));

const listable = computed(() => door.value === "panel");

const { sessionId, armed, pointAt, startNew, onRemoved, sessionsOf } = useBenThread({
	door: () => door.value,
	agentCode: () => ben.value?.code ?? null,
	fromServer: () => data.value?.session ?? null,
});

const listing = ref(false);
const HISTORY_KEY = "benvox.dock.history-collapsed";
const historyCollapsed = ref(readHistoryCollapsed());
function readHistoryCollapsed(): boolean {
	try {
		return localStorage.getItem(HISTORY_KEY) === "1";
	} catch {
		return false;
	}
}
const previewOpen = ref(false);
const { isMobile } = useBreakpoint();
const sidebar = computed(() => !!props.wide && !isMobile.value && listable.value);
const historyVisible = computed(
	() =>
		listable.value &&
		(sidebar.value ? !historyCollapsed.value && !previewOpen.value : listing.value),
);
watch(
	() => props.wide,
	() => {
		listing.value = false;
	},
);
function toggleHistory(): void {
	if (sidebar.value) {
		historyCollapsed.value = !historyCollapsed.value;
		try {
			localStorage.setItem(HISTORY_KEY, historyCollapsed.value ? "1" : "0");
		} catch {
			// The current view still works when browser storage is unavailable.
		}
	} else listing.value = !listing.value;
}
/**
 * A lista ainda em voo. A mesma consulta que `sessionsOf` lê (o vue-query a
 * partilha pela chave), perguntada aqui só pelo estado: sem isto a primeira
 * abertura dizia "Nenhuma conversa ainda." durante o meio segundo da rede.
 */
const { isPending: sessionsPending } = useSessions();
const orbState = ref<OrbState>("idle");
/** Os estados de um turno em voo — os outros três são repouso. */
const BUSY: ReadonlySet<OrbState> = new Set(["thinking", "typing", "acting"]);

/** O compositor desta doca, para o `prefill` de quem chega por "Pedir ao Ben". */
const chatRef = ref<{ prefill: (text: string, send?: boolean) => void } | null>(null);
defineExpose({
	prefill: (text: string, send?: boolean) => chatRef.value?.prefill(text, send),
	/**
	 * "Pedir ao Ben" com `newSession`: a mesma conversa nova do botão do cabeçalho.
	 * MAS NÃO com um turno a correr: a conversa nova desmontava a que trabalhava, e
	 * reabrir a doca a meio de um site de dois minutos dava uma tela vazia (QA-020).
	 * O botão é um atalho; o turno em voo ganha dele.
	 */
	startNew: () => {
		if (!BUSY.has(orbState.value)) void onStartNew();
	},
});

function onState(next: OrbState): void {
	orbState.value = next;
	emit("state", next);
}

function choose(id: string): void {
	pointAt(id);
	listing.value = false;
}

async function onStartNew(): Promise<void> {
	if (await startNew()) listing.value = false;
}

/** Fechar recolhe a lista: a gaveta é escondida e não desmontada, e uma lateral
 *  aberta ontem voltaria aberta amanhã por cima da conversa. */
function onClose(): void {
	listing.value = false;
	emit("close");
}
</script>

<template>
	<!-- `--dock-top`: no telefone o cabeçalho FLUTUA sobre a conversa, como no app do Claude, e quem rola debaixo dele reserva esse espaço. -->
	<div class="relative flex h-full min-h-0 flex-col bg-surface text-fg max-md:[--dock-top:3.75rem]">
		<template v-if="ben">
			<DockHeader
				:agent="ben"
				:state="orbState"
				:listing="historyVisible"
				:door="door"
				:listable="listable && !previewOpen"
				:new-in-sidebar="sidebar && historyVisible"
				:armed="armed"
				:wide="wide"
				:widenable="widenable"
				@toggle-list="toggleHistory"
				@start-new="onStartNew"
				@toggle-wide="emit('toggleWide')"
				@close="onClose"
			/>

			<div class="flex min-h-0 flex-1">
			<!-- Keep the chat mounted: history and width changes must preserve drafts and streams. -->
			<section
				v-if="historyVisible"
				class="flex min-h-0 min-w-0 flex-col"
				:class="sidebar ? 'w-64 shrink-0 border-r border-border bg-bg-subtle' : 'flex-1 pt-[var(--dock-top,0px)]'"
				:aria-label="t('sessions.heading')"
			>
				<div class="flex shrink-0 items-center gap-2 px-3 pt-3 pb-2">
					<h2 class="text-sm font-semibold text-fg">{{ t("sessions.heading") }}</h2>
					<span v-if="!sessionsPending" class="text-xs text-fg-subtle tabular-nums">
						{{ sessionsOf(ben.code).length }}
					</span>
					<Tooltip v-if="sidebar" :text="t('dock.hideHistory')">
						<button type="button" class="ml-auto inline-flex size-9 shrink-0 items-center justify-center rounded-md text-fg-subtle hover:bg-bg-muted focus-visible:outline-2 focus-visible:outline-accent" :aria-label="t('dock.hideHistory')" @click="toggleHistory">
							<Icon name="chevronLeft" class="size-5" />
						</button>
					</Tooltip>
				</div>
				<button v-if="sidebar" type="button" class="mx-3 mb-2 rounded-lg border border-border p-2 text-left text-sm hover:bg-bg-muted" @click="onStartNew">+ {{ t("dock.newConversation") }}</button>
				<SessionList
					class="min-h-0 flex-1"
					fill
					:autofocus="!sidebar"
					:loading="sessionsPending"
					:sessions="sessionsOf(ben.code)"
					:agent-code="ben.code"
					:selected="sessionId"
					@select="choose"
					@removed="onRemoved"
				/>
			</section>

			<div v-show="sidebar || !historyVisible" class="min-h-0 min-w-0 flex-1 overflow-hidden">
				<!-- Chaveado pela thread ANCORADA: mudar de conversa muda o assunto, e sem
				     a chave o chat manteria as bolhas da conversa anterior na tela. -->
				<StreamingChat
					ref="chatRef"
					:key="anchor?.id ?? 'panel'"
					:agent="ben"
					:wide="wide"
					@preview="previewOpen = $event"
					:subject="anchor"
					:session-id="sessionId"
					@session-started="pointAt($event)"
					@state="onState"
					@navigate="emit('navigate', $event)"
					@use-as-draft="emit('useAsDraft', $event)"
					@prepared-draft="emit('preparedDraft', $event)"
				/>
			</div>
			</div>
		</template>

		<!--
			NINGUÉM POSTO — e isto é uma resposta, não um erro.

			Um workspace sem operador ativo existe: alguém desligou a linha. A doca
			diz o que fazer em vez de girar para sempre sobre uma tela vazia.
		-->
		<p v-else-if="!isPending" class="p-4 text-sm text-fg-muted">{{ t("dock.noBen") }}</p>
	</div>
</template>
